import * as React from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Button,
  IconButton,
  MenuItem,
  Menu,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import AdminShell, {
  fieldSx,
  panelSx,
  primaryButtonSx,
  selectMenuProps,
  manageButtonSx,
  tileHeaderSx,
  infoBlockSx,
  sectionTitleSx,
  mutedTextSx,
} from "./AdminShell";
import { getSession } from "../../api/session";
import {
  adminListWorkshops,
  adminUpsertWorkshop,
  adminDeleteWorkshop,
  adminSetActiveWorkshop,
  adminListWorkshopMissions,
  adminSetWorkshopMission,
  adminRemoveWorkshopMission,
  adminListWorkshopGroups,
  adminUpsertWorkshopGroup,
  adminDeleteWorkshopGroup,
  adminListGroupUplinks,
  adminSetGroupUplinkCode,
  adminReopenMissionPlan,
  adminListGroundStations,
  adminListGroupLaunches,
  adminResetGroupLaunch,
  adminListGroupTles,
  adminSetGroupTle,
  adminSetWorkshopGroundStation,
  adminListWorkshopParticipants,
  adminSetParticipantGroup,
  adminRemoveParticipant,
  adminListAllMissions,
  adminListProfiles,
} from "../../api/queries";
import type { AdminGroupLaunchRow, AdminGroupTleRow } from "../../types/launch";
import { tleChecksum } from "../uplink/tle";
import type {
  AdminWorkshopRow,
  AdminWorkshopMissionRow,
  AdminWorkshopGroupRow,
  AdminGroupUplinkRow,
  AdminGroundStationRow,
  AdminParticipantRow,
  AdminMissionRow,
  AdminProfileRow,
} from "../../types/admin";

const EMPTY_FORM = { id: null as string | null, name: "", description: "", startsAt: "", location: "" };

const EMPTY_STATION_FORM = { name: "", lat: "", lon: "", altM: "" };

function toStationForm(row: AdminGroundStationRow | undefined) {
  if (!row) return EMPTY_STATION_FORM;
  return {
    name: row.name,
    lat: row.lat === null ? "" : String(row.lat),
    lon: row.lon === null ? "" : String(row.lon),
    altM: row.alt_m ? String(row.alt_m) : "",
  };
}

const EMPTY_GROUP_FORM = { id: null as string | null, workshopMissionId: "", name: "", accessCode: "", uplinkCode: "" };

const participantChipSx = { bgcolor: "rgba(168,85,247,0.18)", color: "#E9D5FF", fontSize: 11 };

function participantChipLabel(p: AdminParticipantRow) {
  return `${p.mission_name ?? "no mission yet"} · ${p.group_name ?? "no group yet"}`;
}

// A group's second code, which opens the command uplink once the group has
// submitted its mission plan, and whether it has done so.
function GroupUplinkInfo(props: { uplink: AdminGroupUplinkRow | undefined }) {
  const code = props.uplink?.uplink_access_code;
  const submittedAt = props.uplink?.plan_submitted_at;
  return (
    <>
      <Typography sx={mutedTextSx}>Uplink code: {code || "not set yet"}</Typography>
      <Chip
        label={submittedAt ? "Plan submitted" : "Planning"}
        size="small"
        title={
          submittedAt
            ? `Mission plan submitted on ${new Date(submittedAt).toLocaleString("en-US")}; the sheet is locked`
            : "Still working on the mission sheet"
        }
        sx={{
          bgcolor: submittedAt ? "rgba(74,222,128,0.15)" : "rgba(226,232,240,0.1)",
          color: submittedAt ? "#4ADE80" : "rgba(226,232,240,0.6)",
          fontSize: 11,
        }}
      />
    </>
  );
}

// The launch the group chose on its sheet and how far it got.
function GroupLaunchInfo(props: { launch: AdminGroupLaunchRow | undefined }) {
  const l = props.launch;
  const state = !l?.launch_name ? "no launch chosen" : l.has_tle ? "in orbit, tracked" : l.launched_at ? "launched" : "on the pad";
  return <Typography sx={mutedTextSx}>Launch: {l?.launch_name ? `${l.launch_name} (${state})` : state}</Typography>;
}

// What is wrong with one TLE line, in plain words; null if it looks right.
function tleLineProblem(line: string, number: "1" | "2"): string | null {
  if (line.length !== 69) return `Line ${number} has ${line.length} characters; it needs exactly 69.`;
  if (!line.startsWith(`${number} `)) return `Line ${number} must start with "${number} ".`;
  if (String(tleChecksum(line.slice(0, 68))) !== line[68]) {
    return `Line ${number}'s checksum (last digit) should be ${tleChecksum(line.slice(0, 68))}.`;
  }
  return null;
}

const MONO = "'Roboto Mono', 'Courier New', monospace";

// Whether the participant has entered their group's name and access code yet.
function UnlockedChip(props: { unlocked: boolean }) {
  return (
    <Chip
      label={props.unlocked ? "Unlocked" : "Not unlocked yet"}
      size="small"
      title={
        props.unlocked
          ? "Has entered the group's name and access code"
          : "Still has to enter the group's name and access code to open the sheet"
      }
      sx={{
        bgcolor: props.unlocked ? "rgba(74,222,128,0.15)" : "rgba(226,232,240,0.1)",
        color: props.unlocked ? "#4ADE80" : "rgba(226,232,240,0.6)",
        fontSize: 11,
      }}
    />
  );
}

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in local time;
// timestamptz from the DB comes back as an ISO string in UTC.
function toDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toEditForm(w: AdminWorkshopRow) {
  return { id: w.id as string | null, name: w.name, description: w.description, startsAt: toDatetimeLocal(w.starts_at), location: w.location };
}

const addButtonSx = primaryButtonSx({
  flexShrink: 0,
  fontSize: 13,
  color: "#FFFFFF",
  "&.Mui-disabled": { bgcolor: "rgba(124,58,237,0.25)", color: "rgba(255,255,255,0.4)" },
});

// One step of setting a workshop up (missions -> groups -> participants):
// a numbered heading with a one-line explanation, the current entries, and
// the "add" row at the bottom.
function SetupSection(props: { step: number; title: string; hint: string; children: React.ReactNode }) {
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, border: "1px solid rgba(168,85,247,0.25)", bgcolor: "rgba(255,255,255,0.02)" }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              bgcolor: "#7C3AED",
              color: "#FFFFFF",
              fontWeight: 800,
              fontSize: 12.5,
            }}
          >
            {props.step}
          </Box>
          <Stack spacing={0.25}>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 14.5 }}>{props.title}</Typography>
            <Typography sx={mutedTextSx}>{props.hint}</Typography>
          </Stack>
        </Stack>
        {props.children}
      </Stack>
    </Box>
  );
}

// The "add one more" row that closes every setup section.
function AddRow(props: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.04)" }}>
      <Stack spacing={1}>
        <Typography sx={{ color: "#E9D5FF", fontWeight: 800, fontSize: 11.5, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {props.label}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
          {props.children}
        </Stack>
      </Stack>
    </Box>
  );
}

const listRowSx = { py: 0.25, borderBottom: "1px solid rgba(255,255,255,0.06)" };

export default function AdminWorkshops(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const session = React.useMemo(() => getSession(), []);
  const email = session?.email ?? "";

  const [workshops, setWorkshops] = React.useState<AdminWorkshopRow[]>([]);
  const [allMissions, setAllMissions] = React.useState<AdminMissionRow[]>([]);
  const [allProfiles, setAllProfiles] = React.useState<AdminProfileRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [form, setForm] = React.useState(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [showPast, setShowPast] = React.useState(false);

  const [editForm, setEditForm] = React.useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = React.useState(false);

  // One tile is open at a time: "view" (clicking the tile) is read-only,
  // "manage" (the Manage button) edits the workshop info, missions, groups and participants.
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<"view" | "manage">("view");
  const [workshopMissions, setWorkshopMissions] = React.useState<AdminWorkshopMissionRow[]>([]);
  const [groups, setGroups] = React.useState<AdminWorkshopGroupRow[]>([]);
  // Uplink code and plan status per group id.
  const [uplinks, setUplinks] = React.useState<Record<string, AdminGroupUplinkRow>>({});
  // Launch per group id; loaded on its own, so the page still works before
  // supabase/sql/launch.sql is run.
  const [launches, setLaunches] = React.useState<Record<string, AdminGroupLaunchRow>>({});
  // Saved TLE per group id, and the group whose TLE is being edited.
  const [tles, setTles] = React.useState<Record<string, AdminGroupTleRow>>({});
  const [tleEdit, setTleEdit] = React.useState<{ groupId: string; line1: string; line2: string } | null>(null);
  const [tleSaving, setTleSaving] = React.useState(false);
  const [participants, setParticipants] = React.useState<AdminParticipantRow[]>([]);
  const [newMissionId, setNewMissionId] = React.useState("");
  const [groupForm, setGroupForm] = React.useState(EMPTY_GROUP_FORM);
  const [newParticipantEmail, setNewParticipantEmail] = React.useState("");
  const [participantEmailInput, setParticipantEmailInput] = React.useState("");
  const [newParticipantGroupId, setNewParticipantGroupId] = React.useState("");
  // Ground station per workshop id. Loaded on its own, so the rest of the
  // page still works before supabase/sql/tle_ground_station.sql is run.
  const [stations, setStations] = React.useState<Record<string, AdminGroundStationRow>>({});
  const [stationsError, setStationsError] = React.useState<string | null>(null);
  const [stationForm, setStationForm] = React.useState(EMPTY_STATION_FORM);
  const [stationSaving, setStationSaving] = React.useState(false);
  const [groupMenu, setGroupMenu] = React.useState<{ anchorEl: HTMLElement; participant: AdminParticipantRow } | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [{ data: w, error: wErr }, { data: m, error: mErr }, { data: p, error: pErr }] = await Promise.all([
      adminListWorkshops(email),
      adminListAllMissions(email),
      adminListProfiles(email),
    ]);
    setLoading(false);
    if (wErr || mErr || pErr) {
      setError(wErr?.message ?? mErr?.message ?? pErr?.message ?? "Failed to load.");
      return;
    }
    setWorkshops(w ?? []);
    setAllMissions(m ?? []);
    setAllProfiles(p ?? []);

    const { data: gs, error: gsErr } = await adminListGroundStations(email);
    setStationsError(gsErr ? gsErr.message : null);
    const byWorkshop = Object.fromEntries((gs ?? []).map((row) => [row.workshop_id, row]));
    setStations(byWorkshop);
    return byWorkshop;
  }, [email]);

  React.useEffect(() => {
    load();
  }, [load]);

  const loadWorkshopDetail = React.useCallback(
    async (workshopId: string) => {
      const [{ data: wm, error: wmErr }, { data: g, error: gErr }, { data: p, error: pErr }, { data: u, error: uErr }] =
        await Promise.all([
          adminListWorkshopMissions(email, workshopId),
          adminListWorkshopGroups(email, workshopId),
          adminListWorkshopParticipants(email, workshopId),
          adminListGroupUplinks(email, workshopId),
        ]);
      if (wmErr || gErr || pErr || uErr) {
        setError(wmErr?.message ?? gErr?.message ?? pErr?.message ?? uErr?.message ?? "Failed to load workshop detail.");
        return;
      }
      setWorkshopMissions(wm ?? []);
      setGroups(g ?? []);
      setUplinks(Object.fromEntries((u ?? []).map((row) => [row.group_id, row])));
      const { data: l } = await adminListGroupLaunches(email, workshopId);
      setLaunches(Object.fromEntries((l ?? []).map((row) => [row.group_id, row])));
      const { data: t } = await adminListGroupTles(email, workshopId);
      setTles(Object.fromEntries((t ?? []).map((row) => [row.group_id, row])));
      setParticipants(p ?? []);
    },
    [email]
  );

  const openWorkshop = (w: AdminWorkshopRow, nextMode: "view" | "manage") => {
    const closing = selectedId === w.id && mode === nextMode;
    const switching = selectedId !== w.id;
    setSelectedId(closing ? null : w.id);
    setMode(nextMode);
    setError(null);
    setGroupMenu(null);
    setEditForm(closing ? EMPTY_FORM : toEditForm(w));
    setStationForm(closing ? EMPTY_STATION_FORM : toStationForm(stations[w.id]));
    setNewMissionId("");
    setGroupForm(EMPTY_GROUP_FORM);
    setNewParticipantEmail("");
    setParticipantEmailInput("");
    setNewParticipantGroupId("");
    if (switching) {
      setWorkshopMissions([]);
      setGroups([]);
      setUplinks({});
      setLaunches({});
      setParticipants([]);
      loadWorkshopDetail(w.id);
    }
  };

  const saveWorkshop = async () => {
    if (!form.name.trim()) {
      setError("Workshop name is required.");
      return;
    }
    setSaving(true);
    setError(null);

    const { error: saveError } = await adminUpsertWorkshop(email, {
      id: null,
      name: form.name.trim(),
      description: form.description.trim(),
      startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
      location: form.location.trim(),
    });

    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setForm(EMPTY_FORM);
    setShowAddForm(false);
    load();
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) {
      setError("Workshop name is required.");
      return;
    }
    setEditSaving(true);
    setError(null);

    const { error: saveError } = await adminUpsertWorkshop(email, {
      id: editForm.id,
      name: editForm.name.trim(),
      description: editForm.description.trim(),
      startsAt: editForm.startsAt ? new Date(editForm.startsAt).toISOString() : null,
      location: editForm.location.trim(),
    });

    setEditSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    load();
  };

  // Where participants' passes are predicted for (the TLE step of the
  // command uplink). Latitude and longitude are both set or both empty.
  const saveStation = async () => {
    if (!selectedId) return;
    const parse = (text: string) => (text.trim() === "" ? null : Number(text.trim().replace(",", ".")));
    const lat = parse(stationForm.lat);
    const lon = parse(stationForm.lon);
    const altM = parse(stationForm.altM) ?? 0;
    if ([lat, lon, altM].some((v) => v !== null && !Number.isFinite(v))) {
      setError("Latitude, longitude and altitude must be numbers, e.g. 48.8312.");
      return;
    }
    setStationSaving(true);
    setError(null);
    const { error: saveError } = await adminSetWorkshopGroundStation(email, selectedId, {
      name: stationForm.name.trim(),
      lat,
      lon,
      altM,
    });
    setStationSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    const byWorkshop = await load();
    setStationForm(toStationForm(byWorkshop?.[selectedId]));
  };

  const deleteWorkshop = async (id: string) => {
    const { error: delError } = await adminDeleteWorkshop(email, id);
    if (delError) {
      setError(delError.message);
      return;
    }
    if (selectedId === id) setSelectedId(null);
    load();
  };

  const setActive = async (id: string) => {
    const { error: activeError } = await adminSetActiveWorkshop(email, id);
    if (activeError) {
      setError(activeError.message);
      return;
    }
    load();
  };

  const addMissionToWorkshop = async () => {
    if (!selectedId || !newMissionId) return;
    const { error: addError } = await adminSetWorkshopMission(email, selectedId, newMissionId);
    if (addError) {
      setError(addError.message);
      return;
    }
    setNewMissionId("");
    loadWorkshopDetail(selectedId);
  };

  // With a single mission there is nothing to choose, so the group form falls back to it.
  const groupFormMissionId =
    groupForm.workshopMissionId || (workshopMissions.length === 1 ? workshopMissions[0].workshop_mission_id : "");

  const saveGroup = async () => {
    if (!selectedId || !groupFormMissionId || !groupForm.name.trim() || !groupForm.accessCode.trim()) return;
    const { data: savedId, error: saveError } = await adminUpsertWorkshopGroup(email, {
      id: groupForm.id,
      workshopMissionId: groupFormMissionId,
      name: groupForm.name.trim(),
      accessCode: groupForm.accessCode.trim(),
    });
    if (saveError || !savedId) {
      setError(saveError?.message ?? "Couldn't save the group.");
      return;
    }
    // The uplink code is stored separately from the group's name and access code.
    const { error: uplinkError } = await adminSetGroupUplinkCode(email, savedId as string, groupForm.uplinkCode.trim());
    if (uplinkError) {
      setError(uplinkError.message);
      loadWorkshopDetail(selectedId);
      return;
    }
    setGroupForm(EMPTY_GROUP_FORM);
    loadWorkshopDetail(selectedId);
  };

  // For a group that submitted its plan by mistake: the sheet can be edited
  // again, and the members have to submit and enter the uplink code again.
  const reopenPlan = async (group: AdminWorkshopGroupRow) => {
    if (!selectedId) return;
    if (!window.confirm(`Reopen the mission plan of group "${group.group_name}"? Its members can edit the sheet again and will have to submit it and enter the uplink code again.`)) return;
    const { error: reopenError } = await adminReopenMissionPlan(email, group.group_id);
    if (reopenError) {
      setError(reopenError.message);
      return;
    }
    loadWorkshopDetail(selectedId);
  };

  // Back to the launch pad (no launch, no TLE), e.g. to run it again.
  const saveGroupTle = async () => {
    if (!selectedId || !tleEdit) return;
    setTleSaving(true);
    setError(null);
    const { error: tleError } = await adminSetGroupTle(email, tleEdit.groupId, tleEdit.line1, tleEdit.line2);
    setTleSaving(false);
    if (tleError) {
      setError(tleError.message);
      return;
    }
    setTleEdit(null);
    loadWorkshopDetail(selectedId);
  };

  const resetLaunch = async (group: AdminWorkshopGroupRow) => {
    if (!selectedId) return;
    if (!window.confirm(`Reset the launch of group "${group.group_name}"? Its satellite goes back to the launch pad and its TLE is deleted.`)) return;
    const { error: resetError } = await adminResetGroupLaunch(email, group.group_id);
    if (resetError) {
      setError(resetError.message);
      return;
    }
    loadWorkshopDetail(selectedId);
  };

  const deleteGroup = async (group: AdminWorkshopGroupRow) => {
    if (!selectedId) return;
    // The group's sheet goes with it and can't be brought back, so ask first.
    if (!window.confirm(`Delete group "${group.group_name}"? Everything the group filled in on its mission sheet is deleted with it.`)) return;
    const { error: delError } = await adminDeleteWorkshopGroup(email, group.group_id);
    if (delError) {
      setError(delError.message);
      return;
    }
    if (groupForm.id === group.group_id) setGroupForm(EMPTY_GROUP_FORM);
    if (newParticipantGroupId === group.group_id) setNewParticipantGroupId("");
    loadWorkshopDetail(selectedId);
  };

  const removeMissionFromWorkshop = async (workshopMissionId: string) => {
    if (!selectedId) return;
    const { error: removeError } = await adminRemoveWorkshopMission(email, workshopMissionId);
    if (removeError) {
      setError(removeError.message);
      return;
    }
    loadWorkshopDetail(selectedId);
  };

  const addParticipant = async () => {
    if (!selectedId || !newParticipantEmail.trim() || !newParticipantGroupId) return;
    const { error: addError } = await adminSetParticipantGroup(email, newParticipantEmail.trim(), newParticipantGroupId);
    if (addError) {
      setError(addError.message);
      return;
    }
    setNewParticipantEmail("");
    setParticipantEmailInput("");
    loadWorkshopDetail(selectedId);
  };

  // A participant is in one group (and so on one mission) per workshop, so
  // switching is a re-assignment of the same profile rather than a second "add".
  const changeParticipantGroup = async (participant: AdminParticipantRow, groupId: string) => {
    setGroupMenu(null);
    if (!selectedId || participant.group_id === groupId) return;
    const { error: changeError } = await adminSetParticipantGroup(email, participant.participant_email, groupId);
    if (changeError) {
      setError(changeError.message);
      return;
    }
    loadWorkshopDetail(selectedId);
  };

  const removeParticipant = async (profileId: string) => {
    if (!selectedId) return;
    const { error: removeError } = await adminRemoveParticipant(email, profileId);
    if (removeError) {
      setError(removeError.message);
      return;
    }
    loadWorkshopDetail(selectedId);
  };

  const offeredMissionIds = new Set(workshopMissions.map((wm) => wm.mission_id));
  const missionsAvailableToAdd = allMissions.filter((m) => !offeredMissionIds.has(m.id));

  const enrolledEmails = new Set(participants.map((p) => p.participant_email.toLowerCase()));
  const profilesAvailableToAdd = allProfiles.filter((p) => !enrolledEmails.has(p.profile_email.toLowerCase()));

  const visibleWorkshops = React.useMemo(() => {
    const now = Date.now();
    const isPast = (w: AdminWorkshopRow) => w.starts_at !== null && new Date(w.starts_at).getTime() < now;
    return workshops
      .filter((w) => showPast || !isPast(w))
      .slice()
      .sort((a, b) => {
        const at = a.starts_at ? new Date(a.starts_at).getTime() : Infinity;
        const bt = b.starts_at ? new Date(b.starts_at).getTime() : Infinity;
        return at - bt;
      });
  }, [workshops, showPast]);

  return (
    <AdminShell title="Workshop management" currentPage="AdminWorkshops" handlePageChange={handlePageChange}>
      {error && (
        <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {!showAddForm ? (
        <Button
          onClick={() => {
            setForm(EMPTY_FORM);
            setShowAddForm(true);
          }}
          startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
          sx={primaryButtonSx({ alignSelf: "flex-start" })}
        >
          Add new workshop
        </Button>
      ) : (
        <Box sx={panelSx}>
          <Stack spacing={1.5}>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 15 }}>Add workshop</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={fieldSx} size="small" />
              <TextField label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} sx={fieldSx} size="small" />
              <TextField
                label="Starts at"
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
                size="small"
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                sx={fieldSx}
                size="small"
              />
            </Box>
            <Stack direction="row" spacing={1}>
              <Button onClick={saveWorkshop} disabled={saving} sx={primaryButtonSx()}>
                {saving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Add workshop"}
              </Button>
              <Button
                onClick={() => {
                  setForm(EMPTY_FORM);
                  setShowAddForm(false);
                }}
                sx={{ textTransform: "none", fontWeight: 700, color: "rgba(226,232,240,0.7)" }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress sx={{ color: "#C4B5FD" }} />
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => setShowPast((v) => !v)}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "#C4B5FD" }}
            >
              {showPast ? "Hide past workshops" : "Show past workshops"}
            </Button>
          </Stack>
          {visibleWorkshops.map((w) => {
            const open = selectedId === w.id;
            const managing = open && mode === "manage";
            return (
            <Box key={w.id} sx={panelSx}>
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  rowGap={1}
                  // While managing, a stray click on the header must not throw away unsaved edits.
                  onClick={managing ? undefined : () => openWorkshop(w, "view")}
                  sx={managing ? undefined : tileHeaderSx}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ display: "flex", pt: 0.6 }}>
                      {open ? (
                        <ExpandMoreRoundedIcon sx={{ fontSize: 20, color: "#C4B5FD" }} />
                      ) : (
                        <ChevronRightRoundedIcon sx={{ fontSize: 20, color: "rgba(226,232,240,0.6)" }} />
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActive(w.id);
                      }}
                      aria-label="Set active"
                      title="Set as active workshop"
                    >
                      {w.is_active ? (
                        <CheckCircleRoundedIcon sx={{ fontSize: 20, color: "#4ADE80" }} />
                      ) : (
                        <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 20, color: "rgba(226,232,240,0.4)" }} />
                      )}
                    </IconButton>
                    <Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 15 }}>{w.name}</Typography>
                        {w.is_active && (
                          <Chip
                            label="Active"
                            size="small"
                            sx={{ bgcolor: "rgba(74,222,128,0.15)", color: "#4ADE80", fontWeight: 800, fontSize: 11 }}
                          />
                        )}
                      </Stack>
                      <Typography sx={{ color: "rgba(226,232,240,0.6)", fontSize: 12 }}>
                        {w.starts_at ? new Date(w.starts_at).toLocaleString("en-US") : "No date set"}
                        {w.location ? ` · ${w.location}` : ""}
                      </Typography>
                      {w.description && (
                        <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 12.5, mt: 0.5, maxWidth: 520 }}>
                          {w.description}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Button
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        openWorkshop(w, "manage");
                      }}
                      startIcon={managing ? <CloseRoundedIcon sx={{ fontSize: 18 }} /> : <EditRoundedIcon sx={{ fontSize: 18 }} />}
                      sx={manageButtonSx(managing)}
                    >
                      {managing ? "Close" : "Manage"}
                    </Button>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteWorkshop(w.id);
                      }}
                      aria-label="Delete"
                    >
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 17, color: "rgba(248,113,113,0.75)" }} />
                    </IconButton>
                  </Stack>
                </Stack>

                {open && !managing && (
                  <Stack spacing={2} sx={{ pt: 1.5, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                    <Stack spacing={0.75}>
                      <Typography sx={sectionTitleSx}>Ground station</Typography>
                      <Typography sx={stations[w.id]?.lat != null ? { color: "#FFFFFF", fontSize: 13 } : mutedTextSx}>
                        {stations[w.id]?.lat != null
                          ? `${stations[w.id].name || "Unnamed"} (${stations[w.id].lat}°, ${stations[w.id].lon}°)`
                          : "Not set yet. Participants can't send their command until it is."}
                      </Typography>
                    </Stack>
                    <Stack spacing={0.75}>
                      <Typography sx={sectionTitleSx}>Missions offered</Typography>
                      {workshopMissions.length === 0 && <Typography sx={mutedTextSx}>No missions added yet.</Typography>}
                      {workshopMissions.map((wm) => (
                        <Typography key={wm.workshop_mission_id} sx={{ color: "#FFFFFF", fontSize: 13 }}>
                          {wm.mission_name}
                        </Typography>
                      ))}
                    </Stack>
                    <Stack spacing={0.75}>
                      <Typography sx={sectionTitleSx}>Groups</Typography>
                      {groups.length === 0 && <Typography sx={mutedTextSx}>No groups created yet.</Typography>}
                      {groups.map((g) => (
                        <Stack key={g.group_id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                          <Typography sx={{ color: "#FFFFFF", fontSize: 13, flex: 1, minWidth: 140 }}>
                            {g.group_name}{" "}
                            <Box component="span" sx={{ color: "rgba(226,232,240,0.5)" }}>
                              ({g.member_count} {g.member_count === 1 ? "member" : "members"})
                            </Box>
                          </Typography>
                          <Chip label={g.mission_name} size="small" sx={participantChipSx} />
                          <Typography sx={mutedTextSx}>Access code: {g.access_code}</Typography>
                          <GroupUplinkInfo uplink={uplinks[g.group_id]} />
                          <GroupLaunchInfo launch={launches[g.group_id]} />
                        </Stack>
                      ))}
                    </Stack>
                    <Stack spacing={0.75}>
                      <Typography sx={sectionTitleSx}>Participants</Typography>
                      {participants.length === 0 && <Typography sx={mutedTextSx}>No one enrolled yet.</Typography>}
                      {participants.map((p) => (
                        <Stack key={p.profile_id} direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ color: "#FFFFFF", fontSize: 13, flex: 1 }}>
                            {[p.first_name, p.last_name].filter(Boolean).join(" ") || p.participant_email}{" "}
                            <Box component="span" sx={{ color: "rgba(226,232,240,0.5)" }}>({p.participant_email})</Box>
                          </Typography>
                          <Chip label={participantChipLabel(p)} size="small" sx={participantChipSx} />
                          <UnlockedChip unlocked={p.group_unlocked} />
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                )}

                {managing && (
                  <Stack spacing={2.5} sx={{ pt: 1.5, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                    <Box sx={infoBlockSx}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EditRoundedIcon sx={{ fontSize: 15, color: "#C4B5FD" }} />
                          <Typography sx={{ color: "#E9D5FF", fontWeight: 800, fontSize: 12.5, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                            Workshop info
                          </Typography>
                        </Stack>
                        <Stack spacing={1.25}>
                          <TextField
                            label="Name"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            sx={fieldSx}
                            size="small"
                            fullWidth
                          />
                          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25 }}>
                            <TextField
                              label="Location"
                              value={editForm.location}
                              onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                              sx={fieldSx}
                              size="small"
                            />
                            <TextField
                              label="Starts at"
                              type="datetime-local"
                              value={editForm.startsAt}
                              onChange={(e) => setEditForm({ ...editForm, startsAt: e.target.value })}
                              InputLabelProps={{ shrink: true }}
                              sx={fieldSx}
                              size="small"
                            />
                          </Box>
                          <TextField
                            label="Description"
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            sx={fieldSx}
                            size="small"
                            multiline
                            minRows={2}
                            fullWidth
                          />
                        </Stack>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button onClick={saveEdit} disabled={editSaving} sx={primaryButtonSx()}>
                            {editSaving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Save changes"}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                    <Box sx={infoBlockSx}>
                      <Stack spacing={1.5}>
                        <Stack spacing={0.5}>
                          <Typography sx={{ color: "#E9D5FF", fontWeight: 800, fontSize: 12.5, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                            Ground station
                          </Typography>
                          <Typography sx={mutedTextSx}>
                            Where participants' satellite passes are predicted for. Groups can only send their command while
                            their satellite is in view of this station.
                          </Typography>
                        </Stack>
                        {stationsError ? (
                          <Typography sx={{ color: "#FCA5A5", fontSize: 13 }}>
                            Ground stations can't be loaded ({stationsError}). Run supabase/sql/tle_ground_station.sql first.
                          </Typography>
                        ) : (
                          <>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr 1fr" }, gap: 1.25 }}>
                              <TextField
                                label="Name"
                                value={stationForm.name}
                                onChange={(e) => setStationForm({ ...stationForm, name: e.target.value })}
                                placeholder="e.g. Deggendorf"
                                sx={fieldSx}
                                size="small"
                              />
                              <TextField
                                label="Latitude (°)"
                                value={stationForm.lat}
                                onChange={(e) => setStationForm({ ...stationForm, lat: e.target.value })}
                                placeholder="48.8312"
                                sx={fieldSx}
                                size="small"
                              />
                              <TextField
                                label="Longitude (°)"
                                value={stationForm.lon}
                                onChange={(e) => setStationForm({ ...stationForm, lon: e.target.value })}
                                placeholder="12.9606"
                                sx={fieldSx}
                                size="small"
                              />
                              <TextField
                                label="Altitude (m)"
                                value={stationForm.altM}
                                onChange={(e) => setStationForm({ ...stationForm, altM: e.target.value })}
                                placeholder="312"
                                sx={fieldSx}
                                size="small"
                              />
                            </Box>
                            <Typography sx={mutedTextSx}>
                              Latitude north and longitude east are positive, in decimal degrees (e.g. from a map app).
                            </Typography>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button onClick={saveStation} disabled={stationSaving} sx={primaryButtonSx()}>
                                {stationSaving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Save ground station"}
                              </Button>
                            </Stack>
                          </>
                        )}
                      </Stack>
                    </Box>
                    <SetupSection
                      step={1}
                      title="Missions"
                      hint="The missions participants can choose from in this workshop."
                    >
                      {workshopMissions.length === 0 && <Typography sx={mutedTextSx}>No missions added yet.</Typography>}
                      {workshopMissions.map((wm) => (
                        <Stack key={wm.workshop_mission_id} direction="row" spacing={1} alignItems="center" sx={listRowSx}>
                          <Typography sx={{ color: "#FFFFFF", fontSize: 13, flex: 1 }}>{wm.mission_name}</Typography>
                          <IconButton
                            size="small"
                            aria-label="Remove mission"
                            title="Remove this mission (and its groups) from the workshop"
                            onClick={() => removeMissionFromWorkshop(wm.workshop_mission_id)}
                          >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16, color: "rgba(248,113,113,0.75)" }} />
                          </IconButton>
                        </Stack>
                      ))}
                      <AddRow label="Add a mission">
                        <TextField
                          select
                          label="Mission"
                          value={newMissionId}
                          onChange={(e) => setNewMissionId(e.target.value)}
                          disabled={missionsAvailableToAdd.length === 0}
                          sx={{ ...fieldSx, flex: 1 }}
                          size="small"
                          SelectProps={{ MenuProps: selectMenuProps }}
                        >
                          {missionsAvailableToAdd.map((m) => (
                            <MenuItem key={m.id} value={m.id} sx={{ fontSize: 13 }}>
                              {m.name}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Button
                          onClick={addMissionToWorkshop}
                          disabled={!newMissionId}
                          startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
                          sx={addButtonSx}
                        >
                          Add mission
                        </Button>
                      </AddRow>
                      {missionsAvailableToAdd.length === 0 && (
                        <Typography sx={mutedTextSx}>
                          There are no other missions to add. If you want to add more, please create them first on the Missions tab.
                        </Typography>
                      )}
                    </SetupSection>

                    <SetupSection
                      step={2}
                      title="Groups"
                      hint="Participants work on a mission in groups. Each group has its own sheet, which its members open with the group's name and access code. After the group submits its mission plan, its members open the command uplink with the group's name and uplink code."
                    >
                      {workshopMissions.length === 0 ? (
                        <Typography sx={mutedTextSx}>Add a mission first; a group always belongs to one mission.</Typography>
                      ) : (
                        groups.length === 0 && (
                          <Typography sx={mutedTextSx}>No groups yet. Nobody can open a mission sheet until their group exists.</Typography>
                        )
                      )}
                      {groups.map((g) => (
                        <React.Fragment key={g.group_id}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5} sx={listRowSx}>
                          <Typography sx={{ color: "#FFFFFF", fontSize: 13, flex: 1, minWidth: 140 }}>
                            {g.group_name}{" "}
                            <Box component="span" sx={{ color: "rgba(226,232,240,0.5)" }}>
                              ({g.member_count} {g.member_count === 1 ? "member" : "members"})
                            </Box>
                          </Typography>
                          <Chip label={g.mission_name} size="small" sx={participantChipSx} />
                          <Typography sx={mutedTextSx}>Access code: {g.access_code}</Typography>
                          <GroupUplinkInfo uplink={uplinks[g.group_id]} />
                          <GroupLaunchInfo launch={launches[g.group_id]} />
                          <Button
                            size="small"
                            onClick={() =>
                              setTleEdit(
                                tleEdit?.groupId === g.group_id
                                  ? null
                                  : { groupId: g.group_id, line1: tles[g.group_id]?.line1 ?? "", line2: tles[g.group_id]?.line2 ?? "" }
                              )
                            }
                            title="View or change the group's TLE"
                            sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, color: "#C4B5FD", minWidth: 0 }}
                          >
                            {tles[g.group_id]?.line1 ? "TLE" : "Set TLE"}
                          </Button>
                          {launches[g.group_id]?.launched_at && (
                            <Button
                              size="small"
                              onClick={() => resetLaunch(g)}
                              title="Put the group's satellite back on the launch pad"
                              sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, color: "#C4B5FD", minWidth: 0 }}
                            >
                              Reset launch
                            </Button>
                          )}
                          {uplinks[g.group_id]?.plan_submitted_at && (
                            <Button
                              size="small"
                              onClick={() => reopenPlan(g)}
                              title="Let the group edit its mission sheet again"
                              sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, color: "#C4B5FD", minWidth: 0 }}
                            >
                              Reopen plan
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            aria-label="Edit group"
                            title="Rename the group or change its codes"
                            onClick={() =>
                              setGroupForm({
                                id: g.group_id,
                                workshopMissionId: g.workshop_mission_id,
                                name: g.group_name,
                                accessCode: g.access_code,
                                uplinkCode: uplinks[g.group_id]?.uplink_access_code ?? "",
                              })
                            }
                          >
                            <EditRoundedIcon sx={{ fontSize: 16, color: "rgba(226,232,240,0.6)" }} />
                          </IconButton>
                          <IconButton size="small" aria-label="Delete group" title="Delete the group and its sheet" onClick={() => deleteGroup(g)}>
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16, color: "rgba(248,113,113,0.75)" }} />
                          </IconButton>
                        </Stack>
                        {tleEdit?.groupId === g.group_id && (
                          <Box sx={{ ...infoBlockSx, ml: { sm: 2 } }}>
                            <Stack spacing={1.25}>
                              <Typography sx={{ color: "#E9D5FF", fontWeight: 800, fontSize: 12.5 }}>TLE of group {g.group_name}</Typography>
                              <Typography sx={mutedTextSx}>
                                {tles[g.group_id]?.saved_at
                                  ? `Saved ${new Date(tles[g.group_id].saved_at as string).toLocaleString("en-US")}. `
                                  : "This group has no TLE yet; it gets one after its launch. "}
                                Paste or edit the two lines; the group's passes and command window follow the new orbit right away.
                              </Typography>
                              {(["line1", "line2"] as const).map((key, index) => {
                                const problem = tleEdit[key] ? tleLineProblem(tleEdit[key], index === 0 ? "1" : "2") : null;
                                return (
                                  <TextField
                                    key={key}
                                    label={`Line ${index + 1}`}
                                    value={tleEdit[key]}
                                    onChange={(e) => setTleEdit({ ...tleEdit, [key]: e.target.value.replace(/[\r\n]/g, "") })}
                                    error={Boolean(problem)}
                                    helperText={problem ?? " "}
                                    size="small"
                                    fullWidth
                                    inputProps={{ spellCheck: false, maxLength: 80 }}
                                    sx={{
                                      ...fieldSx,
                                      "& input": { fontFamily: MONO, fontSize: 12.5 },
                                      "& .MuiFormHelperText-root": { color: "#FCA5A5", mx: 0.25 },
                                    }}
                                  />
                                );
                              })}
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button onClick={() => setTleEdit(null)} sx={{ textTransform: "none", fontWeight: 700, fontSize: 13, color: "rgba(226,232,240,0.7)" }}>
                                  Cancel
                                </Button>
                                <Button
                                  onClick={saveGroupTle}
                                  disabled={
                                    tleSaving ||
                                    Boolean(tleLineProblem(tleEdit.line1, "1")) ||
                                    Boolean(tleLineProblem(tleEdit.line2, "2")) ||
                                    tleEdit.line1.slice(2, 7) !== tleEdit.line2.slice(2, 7)
                                  }
                                  sx={addButtonSx}
                                >
                                  {tleSaving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Save TLE"}
                                </Button>
                              </Stack>
                              {tleEdit.line1.length === 69 &&
                                tleEdit.line2.length === 69 &&
                                tleEdit.line1.slice(2, 7) !== tleEdit.line2.slice(2, 7) && (
                                  <Typography sx={{ color: "#FCA5A5", fontSize: 12.5 }}>Both lines must carry the same catalog number.</Typography>
                                )}
                            </Stack>
                          </Box>
                        )}
                        </React.Fragment>
                      ))}
                      {workshopMissions.length > 0 && (
                        <AddRow label={groupForm.id ? "Edit group" : "Add a group"}>
                          <TextField
                            select
                            label="Mission"
                            value={groupFormMissionId}
                            onChange={(e) => setGroupForm({ ...groupForm, workshopMissionId: e.target.value })}
                            // A group's sheet belongs to its mission, so it can't be moved afterwards.
                            disabled={groupForm.id !== null}
                            sx={{ ...fieldSx, flex: 1, minWidth: 150 }}
                            size="small"
                            SelectProps={{ MenuProps: selectMenuProps }}
                          >
                            {workshopMissions.map((wm) => (
                              <MenuItem key={wm.workshop_mission_id} value={wm.workshop_mission_id} sx={{ fontSize: 13 }}>
                                {wm.mission_name}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            label="Group name"
                            value={groupForm.name}
                            onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                            sx={{ ...fieldSx, flex: 1, minWidth: 130 }}
                            size="small"
                          />
                          <TextField
                            label="Access code"
                            value={groupForm.accessCode}
                            onChange={(e) => setGroupForm({ ...groupForm, accessCode: e.target.value })}
                            sx={{ ...fieldSx, flex: 1, minWidth: 130 }}
                            size="small"
                          />
                          <TextField
                            label="Uplink code"
                            value={groupForm.uplinkCode}
                            onChange={(e) => setGroupForm({ ...groupForm, uplinkCode: e.target.value })}
                            title="Opens the command uplink once the group has submitted its mission plan"
                            sx={{ ...fieldSx, flex: 1, minWidth: 130 }}
                            size="small"
                          />
                          <Button
                            onClick={saveGroup}
                            disabled={!groupFormMissionId || !groupForm.name.trim() || !groupForm.accessCode.trim()}
                            startIcon={groupForm.id ? undefined : <AddRoundedIcon sx={{ fontSize: 18 }} />}
                            sx={addButtonSx}
                          >
                            {groupForm.id ? "Save group" : "Add group"}
                          </Button>
                          {groupForm.id && (
                            <Button
                              onClick={() => setGroupForm(EMPTY_GROUP_FORM)}
                              sx={{ textTransform: "none", fontWeight: 700, fontSize: 13, color: "rgba(226,232,240,0.7)" }}
                            >
                              Cancel
                            </Button>
                          )}
                        </AddRow>
                      )}
                    </SetupSection>

                    <SetupSection
                      step={3}
                      title="Participants"
                      hint="Plan who works in which group. This does not let anyone in: every participant still has to enter their group's name and access code to open the sheet. Click a participant's group to move them."
                    >
                      {participants.length === 0 && <Typography sx={mutedTextSx}>No one enrolled yet.</Typography>}
                      {participants.map((p) => (
                        <Stack key={p.profile_id} direction="row" spacing={1} alignItems="center" sx={listRowSx}>
                          <Typography sx={{ color: "#FFFFFF", fontSize: 13, flex: 1 }}>
                            {[p.first_name, p.last_name].filter(Boolean).join(" ") || p.participant_email}{" "}
                            <Box component="span" sx={{ color: "rgba(226,232,240,0.5)" }}>({p.participant_email})</Box>
                          </Typography>
                          <Chip
                            label={participantChipLabel(p)}
                            size="small"
                            title="Move to another group"
                            onClick={(e) => setGroupMenu({ anchorEl: e.currentTarget, participant: p })}
                            onDelete={(e) => setGroupMenu({ anchorEl: e.currentTarget.closest(".MuiChip-root") ?? e.currentTarget, participant: p })}
                            deleteIcon={<ArrowDropDownRoundedIcon />}
                            sx={{
                              ...participantChipSx,
                              "&:hover": { bgcolor: "rgba(168,85,247,0.32)" },
                              "& .MuiChip-deleteIcon": { color: "#E9D5FF", "&:hover": { color: "#FFFFFF" } },
                            }}
                          />
                          <UnlockedChip unlocked={p.group_unlocked} />
                          <IconButton size="small" aria-label="Remove participant" title="Remove from this workshop" onClick={() => removeParticipant(p.profile_id)}>
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16, color: "rgba(248,113,113,0.75)" }} />
                          </IconButton>
                        </Stack>
                      ))}
                      {groups.length === 0 ? (
                        <Typography sx={mutedTextSx}>Create a group first; a participant is always added to a group.</Typography>
                      ) : (
                        <AddRow label="Add a participant">
                          <Autocomplete
                            size="small"
                            options={profilesAvailableToAdd.map((p) => p.profile_email)}
                            noOptionsText="No one left to add"
                            value={newParticipantEmail || null}
                            onChange={(_e, value) => setNewParticipantEmail(value ?? "")}
                            inputValue={participantEmailInput}
                            onInputChange={(_e, value) => setParticipantEmailInput(value)}
                            sx={{ ...fieldSx, flex: 1, minWidth: 180 }}
                            slotProps={{ paper: { sx: { bgcolor: "#FFFFFF", color: "#1F2430" } } }}
                            renderInput={(params) => <TextField {...params} label="Participant email" />}
                          />
                          <TextField
                            select
                            label="Group"
                            value={newParticipantGroupId}
                            onChange={(e) => setNewParticipantGroupId(e.target.value)}
                            sx={{ ...fieldSx, flex: 1, minWidth: 180 }}
                            size="small"
                            SelectProps={{ MenuProps: selectMenuProps }}
                          >
                            {groups.map((g) => (
                              <MenuItem key={g.group_id} value={g.group_id} sx={{ fontSize: 13 }}>
                                {g.mission_name} · {g.group_name}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Button
                            onClick={addParticipant}
                            disabled={!newParticipantEmail.trim() || !newParticipantGroupId}
                            startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
                            sx={addButtonSx}
                          >
                            Add participant
                          </Button>
                        </AddRow>
                      )}
                    </SetupSection>
                  </Stack>
                )}
              </Stack>
            </Box>
            );
          })}
        </Stack>
      )}

      <Menu
        anchorEl={groupMenu?.anchorEl ?? null}
        open={groupMenu !== null}
        onClose={() => setGroupMenu(null)}
        {...selectMenuProps}
      >
        {groups.length === 0 && (
          <MenuItem disabled sx={{ fontSize: 13 }}>
            No groups yet
          </MenuItem>
        )}
        {groups.map((g) => (
          <MenuItem
            key={g.group_id}
            selected={g.group_id === groupMenu?.participant.group_id}
            onClick={() => groupMenu && changeParticipantGroup(groupMenu.participant, g.group_id)}
            sx={{ fontSize: 13 }}
          >
            {g.mission_name} · {g.group_name}
          </MenuItem>
        ))}
      </Menu>
    </AdminShell>
  );
}
